import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SupabaseService } from './supabase.service';
import { EmbeddingService } from './embedding.service';
import { CalibrationService, CalibrationCategory } from './calibration.service';
import { CompanyMetadataService, CompanyMetadata } from './company-metadata.service';
import { Job } from '../types/job.types';

export interface ScoreBreakdown {
  category: string;
  score: number;
  matchedSignals: string[];
  weight: number;
}

export interface RankedJob extends Job {
  similarity: number;
  calibrationScore: number;
  experienceScore: number; // Experience match score (0-100)
  totalScore: number;
  matchedSignals: string[];
  isDealbreaker: boolean;
  dealbreakerReason?: string;
  scoreExplanation?: string; // Human-readable explanation of why the score is what it is
  scoreBreakdown?: ScoreBreakdown[]; // Detailed category-by-category breakdown
}

export interface UserExperience {
  workHistory: Array<{
    company?: string;
    title?: string;
    description?: string;
    responsibilities?: string[];
    achievements?: string[];
    startDate?: string;
    endDate?: string;
  }>;
  profileSummary: string;
  experienceYears: number;
  skills: string[];
}

export interface SearchOptions {
  query?: string;
  filters?: {
    location?: string;
    workType?: string;
    level?: string;
    salaryMin?: number;
    salaryMax?: number;
  };
  excludeJobIds?: string[];
  limit?: number;
  minSimilarity?: number;
  forceRefresh?: boolean;  // Skip cache and re-run search
  requireKeywords?: string[];  // Keywords that MUST appear in job text (for explicit requirement searches)
}

export interface CachedSearchMetadata {
  lastSearchAt: Date;
  totalJobsFound: number;
  categoriesSearched: string[];
  lastSignalsHash: string;
}

// Cache validity duration (24 hours)
// Jobs are refreshed daily, so cache for 24h to ensure users see new jobs when they return
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class SemanticJobSearchService {
  /** Count of job matches (updated by job-matches page when jobs load). */
  private _jobMatchesCount = new BehaviorSubject<number>(0);
  jobMatchesCount$: Observable<number> = this._jobMatchesCount.asObservable();

  constructor(
    private supabaseService: SupabaseService,
    private embeddingService: EmbeddingService,
    private calibrationService: CalibrationService,
    private companyMetadataService: CompanyMetadataService
  ) {}

  /** Update the job matches count shown in the header badge. */
  setJobMatchesCount(count: number): void {
    this._jobMatchesCount.next(count);
  }

  /**
   * Main search method - checks cache first, then runs semantic search if needed
   */
  async searchJobs(userId: string, options: SearchOptions = {}): Promise<RankedJob[]> {
    try {
      const {
        query,
        filters = {},
        excludeJobIds = [],
        limit = 50,
        minSimilarity = 0.6,
        forceRefresh = false,
        requireKeywords = []
      } = options;

      // For profile-based search (no query), always fetch excluded job IDs from feedback
      // This ensures jobs user marked as not_interested/applied/hidden stay hidden after refresh
      let effectiveExcludeIds = excludeJobIds;
      if (!query) {
        const feedbackExcludeIds = await this.getExcludedJobIds(userId);
        effectiveExcludeIds = [...new Set([...excludeJobIds, ...feedbackExcludeIds])];
      }

      // For profile-based search (no query), try to use cache first
      if (!query && !forceRefresh) {
        const cachedResults = await this.getCachedMatches(userId, limit, effectiveExcludeIds);
        if (cachedResults) {
          return cachedResults;
        }
        // Cache miss or expired - will run fresh search below
      }

      // Get user's calibration signals
      const calibrationSignals = await this.calibrationService.getSignals(userId);

      // Get user's experience data for experience scoring
      const userExperience = await this.getUserExperience(userId);

      // Get semantic matches
      let semanticMatches: Job[] = [];
      
      if (query) {
        semanticMatches = await this.searchByQuery(query, effectiveExcludeIds, limit, minSimilarity, requireKeywords);
      } else {
        semanticMatches = await this.getProfileBasedMatches(userId, effectiveExcludeIds, limit, minSimilarity);
      }

      // Apply additional filters
      const filteredJobs = this.applyFilters(semanticMatches, filters);

      // Check if we have company_type signals - if so, pre-classify companies with AI
      const companyTypeCategory = calibrationSignals.find(c => c.category === 'company_type');
      const hasCompanyTypeSignals = companyTypeCategory && 
        (companyTypeCategory.excellentFit.length + companyTypeCategory.goodFit.length) > 0;
      
      if (hasCompanyTypeSignals) {
        // Get the target industry from signals
        const targetIndustry = [
          ...companyTypeCategory!.excellentFit.map(s => s.text),
          ...companyTypeCategory!.goodFit.map(s => s.text)
        ].join(' ');
        
        // Pre-classify unique companies with AI
        await this.preClassifyCompanies(filteredJobs, targetIndustry);
      }

      // Score jobs against calibration signals and user experience
      const rankedJobs = this.scoreAndRankJobs(filteredJobs, calibrationSignals, userExperience);

      // Filter out dealbreakers
      const validJobs = rankedJobs.filter(job => !job.isDealbreaker);
      const dealbreakerJobs = rankedJobs.filter(job => job.isDealbreaker);
      
      // Sort by total score and return top results
      const finalResults = validJobs
        .sort((a, b) => b.totalScore - a.totalScore)
        .slice(0, limit);

      // Cache results for profile-based search (not query-based)
      if (!query) {
        const categories = this.extractCategoryFiltersFromSignals(calibrationSignals);
        await this.saveMatchesToCache(userId, finalResults, categories);
      }

      return finalResults;

    } catch {
      return [];
    } finally {
      // Always clear metadata after scoring to prevent stale data
      this.calibrationService.clearCompanyMetadata();
    }
  }

  /**
   * Get cached job matches if they exist and are still valid
   * @param userId - The user ID
   * @param limit - Maximum number of results to return
   * @param excludeJobIds - Job IDs to exclude (e.g., from user feedback)
   */
  async getCachedMatches(userId: string, limit: number = 2000, excludeJobIds: string[] = []): Promise<RankedJob[] | null> {
    try {
      // First check if cache metadata is valid
      const metadata = await this.getCacheMetadata(userId);
      
      if (metadata) {
        // Check cache age using metadata
        const cacheAge = Date.now() - new Date(metadata.lastSearchAt).getTime();
        if (cacheAge > CACHE_TTL_MS) {
          // Cache is stale, clear it and return null to trigger fresh search
          await this.clearUserCache(userId);
          return null;
        }

        // Check if calibration signals have changed
        const currentSignalsHash = await this.getSignalsHash(userId);
        if (currentSignalsHash !== metadata.lastSignalsHash) {
          // Signals changed, clear cache and return null to trigger fresh search
          await this.clearUserCache(userId);
          return null;
        }
      } else {
        // Metadata missing - check if matches exist and validate their age
        // This is a fallback in case metadata table failed to save
        const { data: oldestMatch, error: matchError } = await this.supabaseService
          .getClient()
          .from('semantic_job_matches')
          .select('created_at')
          .eq('user_id', userId)
          .eq('status', 'active')
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        
        // If error or no matches, return null to trigger fresh search
        if (matchError || !oldestMatch) {
          return null;
        }
        
        // Check if matches are stale
        const matchAge = Date.now() - new Date(oldestMatch.created_at).getTime();
        if (matchAge > CACHE_TTL_MS) {
          // Cached matches are stale, clear and return null
          await this.clearUserCache(userId);
          return null;
        }
        // Matches exist and are fresh enough, continue to fetch them
      }

      // Fetch cached matches - no JOIN needed, job data is stored directly
      // Also filter out jobs that user has dismissed via feedback
      let query = this.supabaseService
        .getClient()
        .from('semantic_job_matches')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('total_score', { ascending: false })
        .limit(limit);

      // Exclude jobs from user feedback (not_interested, applied, hidden)
      if (excludeJobIds.length > 0) {
        query = query.not('job_id', 'in', `(${excludeJobIds.join(',')})`);
      }

      const { data, error } = await query;

      if (error) {
        return null;
      }

      if (!data || data.length === 0) return null;

      // Convert to RankedJob format - job data is stored directly in cache
      return data.map((match: any) => ({
        id: match.job_id,
        title: match.job_title,
        company: match.job_company,
        location: match.job_location,
        display_location: match.job_location,
        work_type: match.job_work_type,
        level: match.job_level,
        category: match.job_category,
        company_size: match.job_company_size,
        company_type: match.job_company_type,
        short_description: match.job_short_description,
        full_description: match.job_full_description,
        salary_min: match.job_salary_min,
        salary_max: match.job_salary_max,
        salary_currency: match.job_salary_currency,
        logo_img: match.job_logo_img,
        application_url: match.job_application_url,
        created_at: match.job_created_at,
        // Scoring data
        similarity: match.similarity_score * 100, // Convert to 0-100
        calibrationScore: match.calibration_score,
        experienceScore: match.experience_score || 0,
        totalScore: match.total_score,
        matchedSignals: match.matched_signals || [],
        isDealbreaker: match.is_dealbreaker,
        dealbreakerReason: match.dealbreaker_reason,
        scoreExplanation: match.score_explanation,
        scoreBreakdown: match.score_breakdown || []
      } as RankedJob));

    } catch {
      return null;
    }
  }

  /**
   * Save job matches to cache
   */
  private async saveMatchesToCache(
    userId: string, 
    matches: RankedJob[], 
    categoriesSearched: string[]
  ): Promise<void> {
    try {
      // Clear existing cache first
      await this.clearUserCache(userId);

      if (matches.length === 0) {
        return;
      }

      // Prepare match records with job data included (no JOIN needed on load)
      const matchRecords = matches.map(job => ({
        user_id: userId,
        job_id: job.id,
        // Scoring data
        similarity_score: job.similarity / 100, // Store as 0-1
        calibration_score: job.calibrationScore,
        experience_score: job.experienceScore,
        total_score: job.totalScore,
        matched_signals: job.matchedSignals,
        is_dealbreaker: job.isDealbreaker,
        dealbreaker_reason: job.dealbreakerReason || null,
        score_explanation: job.scoreExplanation || null,
        score_breakdown: job.scoreBreakdown || null,
        status: 'active',
        // Job data (stored directly to avoid JOINs on load)
        job_title: job.title,
        job_company: job.company,
        job_location: job.display_location || job.location,
        job_work_type: job.work_type,
        job_level: job.level,
        job_category: job.category,
        job_short_description: job.short_description,
        job_full_description: job.full_description,
        job_salary_min: job.salary_min,
        job_salary_max: job.salary_max,
        job_salary_currency: job.salary_currency,
        job_logo_img: job.logo_img,
        job_application_url: job.application_url,
        job_created_at: job.created_at,
        job_company_size: job.company_size,
        job_company_type: job.company_type
      }));

      // Upsert matches in smaller batches to avoid 500 errors on large payloads
      const CACHE_BATCH_SIZE = 25;
      for (let i = 0; i < matchRecords.length; i += CACHE_BATCH_SIZE) {
        const batch = matchRecords.slice(i, i + CACHE_BATCH_SIZE);
        try {
          const { error: matchError } = await this.supabaseService
            .getClient()
            .from('semantic_job_matches')
            .upsert(batch, {
              onConflict: 'user_id,job_id',
              ignoreDuplicates: false
            });

          if (matchError) {
            console.warn('[saveMatchesToCache] Batch failed:', matchError.message);
          }
        } catch {
          // Continue with next batch even if one fails
        }
      }

      // Update metadata (optional - table may not exist yet)
      try {
        const signalsHash = await this.getSignalsHash(userId);
        const { error: metaError } = await this.supabaseService
          .getClient()
          .from('semantic_search_metadata')
          .upsert({
            user_id: userId,
            last_search_at: new Date().toISOString(),
            last_signals_hash: signalsHash,
            total_jobs_found: matches.length,
            categories_searched: categoriesSearched,
            updated_at: new Date().toISOString()
          });

      } catch {
        // Silently fail - metadata is optional
      }

    } catch {
    }
  }

  /**
   * Get cache status information for debugging/display
   * Returns cache age in hours and whether it's still valid
   */
  async getCacheStatus(userId: string): Promise<{
    hasCache: boolean;
    cacheAgeHours: number | null;
    isValid: boolean;
    lastSearchAt: Date | null;
    totalJobs: number;
  }> {
    try {
      const metadata = await this.getCacheMetadata(userId);
      
      if (!metadata) {
        // Check if matches exist without metadata
        const { count } = await this.supabaseService
          .getClient()
          .from('semantic_job_matches')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('status', 'active');
        
        return {
          hasCache: (count || 0) > 0,
          cacheAgeHours: null,
          isValid: false,
          lastSearchAt: null,
          totalJobs: count || 0
        };
      }
      
      const cacheAge = Date.now() - new Date(metadata.lastSearchAt).getTime();
      const cacheAgeHours = cacheAge / (60 * 60 * 1000);
      const isValid = cacheAge <= CACHE_TTL_MS;
      
      return {
        hasCache: true,
        cacheAgeHours,
        isValid,
        lastSearchAt: metadata.lastSearchAt,
        totalJobs: metadata.totalJobsFound
      };
    } catch {
      return {
        hasCache: false,
        cacheAgeHours: null,
        isValid: false,
        lastSearchAt: null,
        totalJobs: 0
      };
    }
  }

  /**
   * Get cache metadata for a user
   * Note: Returns null if table doesn't exist or user has no metadata - this is expected
   */
  private async getCacheMetadata(userId: string): Promise<CachedSearchMetadata | null> {
    try {
      const { data, error } = await this.supabaseService
        .getClient()
        .from('semantic_search_metadata')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle(); // Use maybeSingle() to avoid errors when no row exists

      // Silently return null for expected errors (table doesn't exist, no data, etc.)
      if (error || !data) {
        return null;
      }

      return {
        lastSearchAt: new Date(data.last_search_at),
        totalJobsFound: data.total_jobs_found,
        categoriesSearched: data.categories_searched || [],
        lastSignalsHash: data.last_signals_hash
      };
    } catch {
      // Silently fail - metadata is optional for core functionality
      return null;
    }
  }

  /**
   * Clear user's cached job matches
   */
  async clearUserCache(userId: string): Promise<void> {
    try {
      // Use the database function for clean deletion
      const { error } = await this.supabaseService
        .getClient()
        .rpc('clear_semantic_cache', { p_user_id: userId });

      if (error) {
        // Fallback to manual deletion if function doesn't exist yet
        await this.supabaseService
          .getClient()
          .from('semantic_job_matches')
          .delete()
          .eq('user_id', userId);
        
        // Try to delete metadata (may not exist)
        try {
          await this.supabaseService
            .getClient()
            .from('semantic_search_metadata')
            .delete()
            .eq('user_id', userId);
        } catch {
          // Silently ignore - table may not exist
        }
      }
    } catch {
    }
  }

  /**
   * Generate a hash of user's calibration signals for change detection
   */
  private async getSignalsHash(userId: string): Promise<string> {
    try {
      const signals = await this.calibrationService.getSignals(userId);
      
      // Create a simple hash from signal texts
      const signalTexts = signals.flatMap(cat => [
        ...cat.excellentFit.map(s => `e:${s.text}`),
        ...cat.goodFit.map(s => `g:${s.text}`),
        ...cat.borderline.map(s => `b:${s.text}`),
        ...cat.notAFit.map(s => `n:${s.text}`)
      ]).sort().join('|');

      // Simple hash function
      let hash = 0;
      for (let i = 0; i < signalTexts.length; i++) {
        const char = signalTexts.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      
      return hash.toString(16);
    } catch {
      return 'unknown';
    }
  }

  /**
   * Update status of a cached match (e.g., when user hides or saves a job)
   * For hidden/applied jobs, we delete from cache since job_feedback table is the source of truth
   */
  async updateMatchStatus(userId: string, jobId: string, status: 'active' | 'hidden' | 'applied' | 'saved'): Promise<void> {
    try {
      // For hidden or applied jobs, delete from cache entirely
      // The job_feedback table stores the permanent record
      if (status === 'hidden' || status === 'applied') {
        const { error } = await this.supabaseService
          .getClient()
          .from('semantic_job_matches')
          .delete()
          .eq('user_id', userId)
          .eq('job_id', jobId);

        if (error) {
        }
      } else {
        // For saved or active status, just update
        const { error } = await this.supabaseService
          .getClient()
          .from('semantic_job_matches')
          .update({ 
            status, 
            updated_at: new Date().toISOString() 
          })
          .eq('user_id', userId)
          .eq('job_id', jobId);

        if (error) {
        }
      }
    } catch {
    }
  }

  /**
   * Get initial matches for a user based on their profile
   */
  async getInitialMatches(userId: string, forceRefresh: boolean = false): Promise<RankedJob[]> {
    // Location filtering is now done at the DB level via the RPC
    // Use same parameters as refresh button to ensure consistent results
    return this.searchJobs(userId, {
      limit: 2000, // Increased to ensure we don't miss industry-specific jobs with lower similarity
      minSimilarity: 0.4, // Match refresh button - lower threshold to catch more relevant jobs
      forceRefresh
    });
  }

  /**
   * Refine search based on user query
   * @param requireKeywords - Optional keywords that MUST appear in job text (for explicit requirement searches like "jobs that require coding")
   */
  async refineSearch(userId: string, query: string, requireKeywords?: string[]): Promise<RankedJob[]> {
    // Get jobs user has already seen/rejected
    const excludeJobIds = await this.getExcludedJobIds(userId);

    return this.searchJobs(userId, {
      query,
      excludeJobIds,
      limit: 30,
      minSimilarity: 0.6,
      requireKeywords
    });
  }

  /**
   * Search jobs by semantic query
   * @param requireKeywords - Keywords that MUST appear in job text (for explicit requirement searches)
   */
  private async searchByQuery(
    query: string, 
    excludeJobIds: string[] = [], 
    limit: number = 50,
    minSimilarity: number = 0.6,
    requireKeywords: string[] = []
  ): Promise<Job[]> {
    try {
      // Get or create embedding for the query
      const queryEmbedding = await this.embeddingService.getOrCreateQueryEmbedding(query);

      // Note: Query-based search doesn't apply filters since we want to match the query literally
      // If user searches for "backend engineer" but their profile is designer, show backend jobs

      // If we have requireKeywords, fetch more results since we'll filter some out
      const fetchLimit = requireKeywords.length > 0 ? limit * 3 : limit;

      // Use the database function for vector similarity search
      const { data, error } = await this.supabaseService
        .getClient()
        .rpc('match_jobs_semantic', {
          query_embedding: JSON.stringify(queryEmbedding),
          exclude_job_ids: excludeJobIds,
          match_count: fetchLimit,
          filter_categories: null, // Don't filter for query-based search
          filter_levels: null,
          filter_work_types: null
        });

      if (error) {
        throw error;
      }

      // Filter by minimum similarity and fetch full job data
      const similarJobs = (data || []).filter((job: any) => job.similarity >= minSimilarity);
      
      if (similarJobs.length === 0) {
        return [];
      }

      // Get full job details
      const jobIds = similarJobs.map((job: any) => job.job_id);
      const fullJobs = await this.getJobsByIds(jobIds);

      // Add similarity scores to jobs
      let jobsWithScores = fullJobs.map(job => ({
        ...job,
        similarity: similarJobs.find((sj: any) => sj.job_id === job.id)?.similarity || 0
      }));

      // Apply keyword filtering if requireKeywords is provided
      if (requireKeywords.length > 0) {
        jobsWithScores = this.filterByRequiredKeywords(jobsWithScores, requireKeywords);
      }

      // Return up to the requested limit
      return jobsWithScores.slice(0, limit);

    } catch {
      return [];
    }
  }

  /**
   * Filter jobs to only include those that contain at least one of the required keywords
   * in their description, requirements, responsibilities, or skills.
   */
  private filterByRequiredKeywords<T extends Job>(jobs: T[], keywords: string[]): T[] {
    const lowerKeywords = keywords.map(kw => kw.toLowerCase());
    
    return jobs.filter(job => {
      // Build a searchable text from all relevant job fields
      const searchableText = [
        job.short_description || '',
        job.full_description || '',
        ...(job.requirements || []),
        ...(job.responsibilities || []),
        ...(job.skills || []),
        job.title || ''
      ].join(' ').toLowerCase();

      // Check if ANY of the required keywords appear in the job text
      return lowerKeywords.some(keyword => searchableText.includes(keyword));
    });
  }

  /**
   * Extract job category filters from calibration signals
   * 
   * We ONLY filter by category (job type), not by level or work_type because:
   * - Level is inconsistent across companies ("Senior" at startup ≠ "Senior" at FAANG)
   * - Work type preferences are "soft" - someone preferring Remote might still consider Hybrid
   * - Category is a "hard" filter - a Product Designer shouldn't see Backend Engineer jobs
   * 
   * Level and work_type preferences are handled by calibration scoring instead.
   */
  private extractCategoryFiltersFromSignals(calibrationSignals: CalibrationCategory[]): string[] {
    const categories: string[] = [];

    // Find the 'role' category signals
    const roleCategory = calibrationSignals.find(cat => cat.category === 'role');
    if (roleCategory) {
      // Extract from excellent and good fit signals only
      // (borderline and not_a_fit are for calibration scoring, not filtering)
      const roleSignals = [
        ...roleCategory.excellentFit.map(s => s.text),
        ...roleCategory.goodFit.map(s => s.text)
      ];

      roleSignals.forEach(signal => {
        const lowerSignal = signal.toLowerCase();
        
        // Map role signals to job categories
        // IMPORTANT: These MUST match the category values in the jobs table exactly
        // Categories from job-category.service.ts:
        // "Product Design", "UI/UX Design", "UX Research", "Brand Design", 
        // "Design Engineering", "Design Leadership", "Graphic Design", 
        // "Interaction Design", "Motion Design", "Web Design"
        
        if (lowerSignal.includes('product designer') || lowerSignal.includes('product design')) {
          categories.push('Product Design');
        }
        if (lowerSignal.includes('ux designer') || lowerSignal.includes('ui/ux') || 
            lowerSignal.includes('ux design') || lowerSignal.includes('ui designer') || 
            lowerSignal.includes('ui design')) {
          categories.push('UI/UX Design');
        }
        if (lowerSignal.includes('ux research') || lowerSignal.includes('user research')) {
          categories.push('UX Research');
        }
        if (lowerSignal.includes('graphic designer') || lowerSignal.includes('graphic design')) {
          categories.push('Graphic Design');
        }
        if (lowerSignal.includes('web designer') || lowerSignal.includes('web design')) {
          categories.push('Web Design');
        }
        if (lowerSignal.includes('brand designer') || lowerSignal.includes('brand design')) {
          categories.push('Brand Design');
        }
        if (lowerSignal.includes('motion designer') || lowerSignal.includes('motion design')) {
          categories.push('Motion Design');
        }
        if (lowerSignal.includes('interaction designer') || lowerSignal.includes('interaction design')) {
          categories.push('Interaction Design');
        }
        if (lowerSignal.includes('design manager') || lowerSignal.includes('head of design') || 
            lowerSignal.includes('design director') || lowerSignal.includes('design leadership') ||
            lowerSignal.includes('vp of design') || lowerSignal.includes('chief design')) {
          categories.push('Design Leadership');
        }
        if (lowerSignal.includes('design engineer') || lowerSignal.includes('design system') ||
            lowerSignal.includes('front-end') || lowerSignal.includes('frontend')) {
          categories.push('Design Engineering');
        }
        
        // Lead/Senior Product Designer should map to Product Design category
        if (lowerSignal.includes('lead') && (lowerSignal.includes('product') || lowerSignal.includes('ux'))) {
          categories.push('Product Design', 'UI/UX Design');
        }
        
        // Full-stack developer with design focus
        if (lowerSignal.includes('full-stack') || lowerSignal.includes('fullstack')) {
          categories.push('Design Engineering');
        }
      });
    }

    // Remove duplicates
    const uniqueCategories = [...new Set(categories)];
    return uniqueCategories;
  }

  /**
   * Extract title keywords from role signals for additional title-based filtering
   * This catches jobs that are miscategorized but have matching titles
   */
  private extractTitleKeywordsFromSignals(calibrationSignals: CalibrationCategory[]): string[] {
    const titleKeywords: string[] = [];

    const roleCategory = calibrationSignals.find(cat => cat.category === 'role');
    if (roleCategory) {
      const roleSignals = [
        ...roleCategory.excellentFit.map(s => s.text),
        ...roleCategory.goodFit.map(s => s.text)
      ];

      roleSignals.forEach(signal => {
        const lowerSignal = signal.toLowerCase().trim();
        // Add the signal itself as a title keyword if it looks like a job title
        if (lowerSignal.includes('designer') || lowerSignal.includes('developer') || 
            lowerSignal.includes('engineer') || lowerSignal.includes('manager') ||
            lowerSignal.includes('director') || lowerSignal.includes('lead') ||
            lowerSignal.includes('researcher') || lowerSignal.includes('artist')) {
          titleKeywords.push(lowerSignal);
        }
      });
    }

    const uniqueKeywords = [...new Set(titleKeywords)];
    return uniqueKeywords;
  }

  /**
   * Check if user has company_type signals (industry preferences)
   * When true, we need to fetch more jobs to ensure we catch industry-specific matches
   */
  private hasCompanyTypeSignals(calibrationSignals: CalibrationCategory[]): boolean {
    const companyTypeCategory = calibrationSignals.find(cat => cat.category === 'company_type');
    if (!companyTypeCategory) return false;
    
    const totalSignals = 
      companyTypeCategory.excellentFit.length + 
      companyTypeCategory.goodFit.length + 
      companyTypeCategory.borderline.length;
    
    return totalSignals > 0;
  }

  /**
   * Extract location filters from chat-sourced calibration signals
   * Returns work types and location keywords for DB filtering
   * Note: location category contains geographic locations AND remote
   * work_type category contains onsite/hybrid
   */
  private extractLocationFiltersFromSignals(signals: any[]): {
    workTypes: string[] | null;
    locationKeywords: string[] | null;
  } {
    const locationCategory = signals.find(s => s.category === 'location');
    const workTypeCategory = signals.find(s => s.category === 'work_type');
    
    // Get chat-sourced signals from both categories
    const locationChatSignals = locationCategory?.excellentFit?.filter((s: any) => s.source === 'chat') || [];
    const workTypeChatSignals = workTypeCategory?.excellentFit?.filter((s: any) => s.source === 'chat') || [];
    
    if (locationChatSignals.length === 0 && workTypeChatSignals.length === 0) {
      return { workTypes: null, locationKeywords: null };
    }

    const workTypes: string[] = [];
    const locationKeywords: string[] = [];
    
    // Extract work types from work_type category (onsite/hybrid)
    for (const signal of workTypeChatSignals) {
      const text = signal.text.toLowerCase();
      if (text.includes('onsite') || text.includes('on-site') || text.includes('in-office')) {
        if (!workTypes.includes('onsite')) workTypes.push('onsite');
      } else if (text.includes('hybrid')) {
        if (!workTypes.includes('hybrid')) workTypes.push('hybrid');
      }
    }

    // Continent to countries mapping for expansion
    const continentExpansion: Record<string, string[]> = {
      'europe': [
        // Western Europe
        'United Kingdom', 'UK', 'GB', 'Great Britain', 'Britain', 'England', 
        'Scotland', 'Wales', 'Northern Ireland', 'Ireland', 'Republic of Ireland',
        'France', 'Germany', 'Netherlands', 'Holland', 'Belgium', 'Luxembourg', 
        'Austria', 'Switzerland', 'Liechtenstein', 'Monaco',
        // Southern Europe
        'Spain', 'Portugal', 'Italy', 'Greece', 'Malta', 'Cyprus', 'Andorra',
        'San Marino', 'Vatican', 'Gibraltar',
        // Northern Europe (Nordics)
        'Sweden', 'Norway', 'Denmark', 'Finland', 'Iceland', 'Faroe Islands',
        // Central Europe
        'Poland', 'Czech Republic', 'Czechia', 'Czech', 'Slovakia', 'Hungary', 'Slovenia',
        // Eastern Europe / Balkans
        'Romania', 'Bulgaria', 'Croatia', 'Serbia', 'Bosnia', 'Herzegovina',
        'Bosnia and Herzegovina', 'Montenegro', 'North Macedonia', 'Macedonia',
        'Albania', 'Kosovo', 'Moldova', 'Ukraine', 'Belarus',
        // Baltic States
        'Estonia', 'Latvia', 'Lithuania',
        // Caucasus (safe with exact geocoded matching)
        'Georgia', 'Armenia', 'Azerbaijan',
        // Major European cities
        'London', 'Paris', 'Berlin', 'Amsterdam', 'Barcelona', 'Madrid', 'Rome',
        'Milan', 'Munich', 'Dublin', 'Lisbon', 'Vienna', 'Prague', 'Warsaw',
        'Brussels', 'Copenhagen', 'Stockholm', 'Oslo', 'Helsinki', 'Zurich',
        'Geneva', 'Edinburgh', 'Manchester', 'Birmingham', 'Frankfurt', 'Hamburg',
        'Lyon', 'Marseille', 'Valencia', 'Seville', 'Naples', 'Turin', 'Florence',
        'Krakow', 'Budapest', 'Bucharest', 'Sofia', 'Athens', 'Thessaloniki',
        'Tallinn', 'Riga', 'Vilnius', 'Tbilisi', 'Yerevan', 'Baku', 'Kyiv', 'Minsk'
      ],
      'asia': [
        'Japan', 'Singapore', 'India', 'China', 'South Korea', 'Korea', 'Taiwan', 
        'Hong Kong', 'Thailand', 'Vietnam', 'Malaysia', 'Indonesia', 'Philippines',
        'Pakistan', 'Bangladesh', 'Sri Lanka', 'Nepal', 'UAE', 'Dubai', 'Israel',
        'Turkey', 'Saudi Arabia', 'Qatar', 'Kuwait', 'Bahrain', 'Oman'
      ],
      'latam': [
        'Brazil', 'Mexico', 'Argentina', 'Colombia', 'Chile', 'Peru', 
        'Costa Rica', 'Uruguay', 'Ecuador', 'Venezuela', 'Panama', 'Guatemala',
        'Honduras', 'El Salvador', 'Nicaragua', 'Paraguay', 'Bolivia',
        'Dominican Republic', 'Puerto Rico', 'Cuba', 'Jamaica'
      ],
      'latin america': [
        'Brazil', 'Mexico', 'Argentina', 'Colombia', 'Chile', 'Peru', 
        'Costa Rica', 'Uruguay', 'Ecuador', 'Venezuela', 'Panama', 'Guatemala',
        'Honduras', 'El Salvador', 'Nicaragua', 'Paraguay', 'Bolivia',
        'Dominican Republic', 'Puerto Rico', 'Cuba', 'Jamaica'
      ],
      'north america': [
        'United States', 'USA', 'US', 'Canada'
      ],
      'oceania': [
        'Australia', 'New Zealand'
      ],
      'africa': [
        'South Africa', 'Nigeria', 'Kenya', 'Egypt', 'Morocco', 'Ghana',
        'Tunisia', 'Rwanda', 'Ethiopia', 'Tanzania', 'Uganda'
      ]
    };

    // Extract from location category (geographic locations + remote)
    for (const signal of locationChatSignals) {
      const text = signal.text.toLowerCase();
      
      // Extract remote work type from location category
      // Note: onsite/hybrid are NOT here - they're in work_type category
      if (text.includes('remote')) {
        if (!workTypes.includes('remote')) workTypes.push('remote');
      }
      
      // Extract location keywords (remove common words and non-location terms)
      const nonLocationWords = [
        'fully', 'completely', '100%', 'work', 'preference', 'flexible',
        'anywhere', 'worldwide', 'global', 'international'
      ];
      const words = text
        .replace(/jobs?|in|or|and|the|for|at|remote|onsite|on-site|hybrid|only/gi, ' ')
        .split(/\s+/)
        .filter((w: string) => w.length > 2 && !nonLocationWords.includes(w.toLowerCase()));
      
      // Expand continent keywords to actual countries
      for (const word of words) {
        const expansion = continentExpansion[word.toLowerCase()];
        if (expansion) {
          locationKeywords.push(...expansion);
        } else {
          locationKeywords.push(word);
        }
      }
    }

    return {
      workTypes: workTypes.length > 0 ? workTypes : null,
      locationKeywords: locationKeywords.length > 0 ? locationKeywords : null
    };
  }

  /**
   * Get matches based on user's profile embedding
   */
  private async getProfileBasedMatches(
    userId: string, 
    excludeJobIds: string[] = [], 
    limit: number = 50,
    minSimilarity: number = 0.6
  ): Promise<Job[]> {
    try {
      const userEmbedding = await this.embeddingService.getUserEmbedding(userId);

      if (!userEmbedding) {
        throw new Error('User profile embedding not found. Please complete your onboarding to enable job matching.');
      }

      // Get user's calibration signals to extract role/category preferences
      const calibrationSignals = await this.calibrationService.getSignals(userId);
      const categories = this.extractCategoryFiltersFromSignals(calibrationSignals);
      
      // First, count total jobs in filtered categories to show coverage
      let totalJobsInCategories = 0;
      if (categories.length > 0) {
        const { count, error: countError } = await this.supabaseService
          .getClient()
          .from('jobs')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'published')
          .eq('is_expired', false)
          .not('embedding_generated_at', 'is', null)
          .in('category', categories);
        
        if (!countError && count !== null) {
          totalJobsInCategories = count;
        }
      } else {
        const { count, error: countError } = await this.supabaseService
          .getClient()
          .from('jobs')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'published')
          .eq('is_expired', false)
          .not('embedding_generated_at', 'is', null);
        
        if (!countError && count !== null) {
          totalJobsInCategories = count;
        }
      }

      // Extract location filters from chat signals
      const locationFilters = this.extractLocationFiltersFromSignals(calibrationSignals);
      
      // Extract title keywords for additional title-based filtering
      // This catches jobs that are miscategorized but have matching titles
      const titleKeywords = this.extractTitleKeywordsFromSignals(calibrationSignals);
      
      // Check if user has company_type signals (industry preferences like hr, crypto)
      // If so, we need to fetch MORE jobs to ensure we don't miss industry matches
      // that might have lower semantic similarity
      const hasIndustryPreference = this.hasCompanyTypeSignals(calibrationSignals);
      const effectiveLimit = hasIndustryPreference 
        ? Math.max(limit, totalJobsInCategories)
        : limit;

      // Use the database function for vector similarity search
      // Pass embedding as array - Supabase will convert to vector type
      const { data, error } = await this.supabaseService
        .getClient()
        .rpc('match_jobs_semantic', {
          query_embedding: userEmbedding,
          exclude_job_ids: excludeJobIds,
          match_count: effectiveLimit,
          filter_categories: categories.length > 0 ? categories : null,
          filter_levels: null,
          filter_work_types: locationFilters.workTypes,
          filter_location_keywords: locationFilters.locationKeywords,
          filter_title_keywords: titleKeywords.length > 0 ? titleKeywords : null
        });

      if (error) {
        throw error;
      }

      if (!data || data.length === 0) return [];

      const similarJobs = (data || []).filter((job: any) => job.similarity >= minSimilarity);

      if (similarJobs.length === 0) {
        const fallbackJobs = data.slice(0, limit);
        
        const jobIds = fallbackJobs.map((job: any) => job.job_id);
        const fullJobs = await this.getJobsByIds(jobIds);
        
        return fullJobs.map(job => ({
          ...job,
          similarity: fallbackJobs.find((sj: any) => sj.job_id === job.id)?.similarity || 0
        }));
      }

      // Get full job details (batched to handle >1000 jobs)
      const jobIds = similarJobs.map((job: any) => job.job_id);
      const fullJobs = await this.getJobsByIds(jobIds);

      // Add similarity scores to jobs
      const result = fullJobs.map(job => ({
        ...job,
        similarity: similarJobs.find((sj: any) => sj.job_id === job.id)?.similarity || 0
      }));

      return result;

    } catch (error) {
      throw error; // Don't fallback, let caller handle the error
    }
  }

  /**
   * Apply additional filters to jobs
   */
  private applyFilters(jobs: Job[], filters: SearchOptions['filters'] = {}): Job[] {
    let filtered = jobs;

    if (filters.location) {
      filtered = filtered.filter(job => 
        job.location?.toLowerCase().includes(filters.location!.toLowerCase()) ||
        job.display_location?.toLowerCase().includes(filters.location!.toLowerCase())
      );
    }

    if (filters.workType) {
      filtered = filtered.filter(job => 
        job.work_type?.toLowerCase() === filters.workType!.toLowerCase() ||
        job.employment_type?.toLowerCase() === filters.workType!.toLowerCase()
      );
    }

    if (filters.level) {
      filtered = filtered.filter(job => 
        job.level?.toLowerCase().includes(filters.level!.toLowerCase())
      );
    }

    if (filters.salaryMin) {
      filtered = filtered.filter(job => 
        job.salary_min && job.salary_min >= filters.salaryMin!
      );
    }

    if (filters.salaryMax) {
      filtered = filtered.filter(job => 
        job.salary_max && job.salary_max <= filters.salaryMax!
      );
    }

    return filtered;
  }

  /**
   * Score and rank jobs using calibration signals and user experience
   */
  private scoreAndRankJobs(
    jobs: Job[], 
    calibrationSignals: CalibrationCategory[],
    userExperience: UserExperience
  ): RankedJob[] {
    // Convert to Map for easy lookup in explanation generation
    const signalsMap = new Map<string, any[]>();
    calibrationSignals.forEach(cat => {
      const allSignals = [
        ...cat.excellentFit.map(s => ({ ...s, tier: 'excellent_fit' })),
        ...cat.goodFit.map(s => ({ ...s, tier: 'good_fit' })),
        ...cat.borderline.map(s => ({ ...s, tier: 'borderline' })),
        ...cat.notAFit.map(s => ({ ...s, tier: 'not_a_fit' }))
      ];
      signalsMap.set(cat.category, allSignals);
    });
    
    // Track AI company names for debug logging
    const aiCompanyNames = new Set<string>();
    calibrationSignals.forEach(cat => {
      if (cat.category === 'company_type') {
        // We'll populate this from metadata if available
      }
    });
    
    const ranked = jobs.map((job, idx) => {
      // Enable debug mode for first 10 jobs
      const jobScore = this.calibrationService.scoreJob(job, calibrationSignals, idx < 10);
      const similarity = (job as any).similarity || 0;
      
      // Similarity is 0-1 from cosine similarity, convert to 0-100 scale
      const similarityScore = similarity * 100; // Convert to 0-100
      const calibrationScore = jobScore.totalScore; // Already 0-100
      
      // Calculate experience score based on work history match
      const experienceScore = this.calculateExperienceScore(job, userExperience, idx < 2);
      
      // Combine scores: similarity 25%, calibration 55%, experience 20%
      const totalScore = (similarityScore * 0.25) + (calibrationScore * 0.55) + (experienceScore * 0.20);

      // Generate human-readable explanation for the score
      const scoreExplanation = this.generateScoreExplanation(
        job, 
        jobScore.breakdown, 
        similarityScore, 
        calibrationScore, 
        totalScore,
        signalsMap
      );

      return {
        ...job,
        similarity: similarityScore, // Store as 0-100 for consistency
        calibrationScore,
        experienceScore,
        totalScore,
        matchedSignals: jobScore.breakdown.flatMap(b => b.matchedSignals),
        isDealbreaker: jobScore.isDealbreaker,
        dealbreakerReason: jobScore.dealbreakerReason,
        scoreExplanation,
        scoreBreakdown: jobScore.breakdown
      };
    });

    return ranked;
  }

  /**
   * Generate a human-readable explanation for the match score.
   * Outputs one line per category in "Label: Description" format, newline-separated,
   * so the frontend can render as <ul><li>...</li></ul>.
   */
  private generateScoreExplanation(
    job: Job,
    breakdown: { category: string; score: number; matchedSignals: string[]; weight: number }[],
    similarityScore: number,
    calibrationScore: number,
    totalScore: number,
    signals: Map<string, any[]>
  ): string {
    const lines: string[] = [];

    const getUserPrefs = (category: string): string[] => {
      const categorySignals = signals.get(category) || [];
      return categorySignals
        .filter((s: any) => s.tier === 'excellent_fit' || s.tier === 'good_fit')
        .map((s: any) => s.text)
        .slice(0, 3);
    };

    const formatCategoryLabel = (cat: string): string => {
      if (cat === 'location') return 'Location';
      if (cat === 'role') return 'Work type';
      if (cat === 'skills') return 'Skills';
      return cat.charAt(0).toUpperCase() + cat.slice(1);
    };

    const locationBreakdown = breakdown.find(b => b.category === 'location');
    const roleBreakdown = breakdown.find(b => b.category === 'role');
    const skillsBreakdown = breakdown.find(b => b.category === 'skills');

    if (totalScore >= 70) {
      if (locationBreakdown?.matchedSignals.length) {
        const loc = locationBreakdown.matchedSignals.join(', ');
        lines.push(`Location: Matches your preferences (${loc})`);
      }
      if (roleBreakdown?.matchedSignals.length) {
        lines.push('Work type: Lines up with what you want');
      }
      if (skillsBreakdown?.matchedSignals.length) {
        const n = skillsBreakdown.matchedSignals.length;
        lines.push(n === 1
          ? 'Skills: One of your key skills matches their requirements'
          : `Skills: ${n} of your skills match their requirements`);
      }
      if (lines.length === 0) {
        lines.push('Your background fits this role well');
      }
    } else if (totalScore >= 40) {
      const matchedCategories = breakdown.filter(b => b.matchedSignals.length > 0);
      const unmatchedCategories = breakdown.filter(b => b.matchedSignals.length === 0 && b.weight > 0.1);

      if (matchedCategories.length > 0) {
        for (const b of matchedCategories) {
          const label = formatCategoryLabel(b.category);
          if (b.category === 'skills') {
            lines.push(`${label}: ${b.matchedSignals.length} skills match`);
          } else {
            lines.push(`${label}: Some overlap with your preferences`);
          }
        }
      }
      if (unmatchedCategories.length > 0) {
        for (const cat of unmatchedCategories) {
          const label = formatCategoryLabel(cat.category);
          const prefs = getUserPrefs(cat.category);
          if (prefs.length > 0) {
            lines.push(`${label}: Doesn't quite match (you want e.g. ${prefs.join(', ')})`);
          } else {
            lines.push(`${label}: Doesn't match what you're looking for`);
          }
        }
      }
    } else {
      if (locationBreakdown && locationBreakdown.matchedSignals.length === 0 && locationBreakdown.weight > 0) {
        const jobLoc = job.display_location || job.location || 'unspecified location';
        const jobType = job.work_type || 'unspecified work type';
        const userLocPrefs = getUserPrefs('location');
        if (userLocPrefs.length > 0) {
          lines.push(`Location: You want ${userLocPrefs.join(' or ')}, but this is ${jobType} in ${jobLoc}`);
        } else {
          lines.push(`Location: ${jobType} in ${jobLoc} may not fit`);
        }
      }

      if (roleBreakdown && roleBreakdown.matchedSignals.length === 0 && roleBreakdown.weight > 0) {
        const userRolePrefs = getUserPrefs('role');
        if (userRolePrefs.length > 0) {
          lines.push(`Work type: You're aiming for "${userRolePrefs.join('", "')}", this is "${job.title}"`);
        } else {
          lines.push(`Work type: Role (${job.title}) doesn't match what you're after`);
        }
      }

      if (skillsBreakdown && skillsBreakdown.matchedSignals.length === 0 && skillsBreakdown.weight > 0) {
        const jobSkills = job.skills?.slice(0, 3).join(', ') || 'unknown skills';
        lines.push(`Skills: Their requirements (${jobSkills}) don't line up well`);
      }

      if (similarityScore > 60 && calibrationScore < 20 && lines.length === 0) {
        lines.push('Your profile is close, but details don\'t quite match your preferences');
      }

      if (lines.length === 0) {
        lines.push('Not much overlap with what you\'re looking for');
      }
    }

    return lines.join('\n');
  }

  /**
   * Get jobs by IDs with full details
   * Uses batching to overcome Supabase's 1000 row limit
   */
  private async getJobsByIds(jobIds: string[]): Promise<Job[]> {
    try {
      if (jobIds.length === 0) {
        return [];
      }

      // Use smaller batches to avoid query timeouts and URL length limits
      // Reduced to 30 IDs per batch to avoid 500 errors
      const BATCH_SIZE = 30;
      const batches: string[][] = [];
      
      for (let i = 0; i < jobIds.length; i += BATCH_SIZE) {
        batches.push(jobIds.slice(i, i + BATCH_SIZE));
      }

      // Fetch batches in parallel (reduced concurrency to avoid overwhelming Supabase)
      const MAX_CONCURRENT = 3;
      const allResults: any[] = [];
      
      for (let i = 0; i < batches.length; i += MAX_CONCURRENT) {
        const currentBatches = batches.slice(i, i + MAX_CONCURRENT);
        
        const batchResults = await Promise.all(
          currentBatches.map(async (batchIds, batchIndex) => {
            const maxRetries = 3;
            let lastError: Error | null = null;
            
            for (let attempt = 1; attempt <= maxRetries; attempt++) {
              try {
                const { data, error } = await this.supabaseService
                  .getClient()
                  .from('jobs')
                  .select(`
                    *,
                    job_locations (
                      city_geocoded,
                      country_geocoded,
                      raw_location_string
                    )
                  `)
                  .in('id', batchIds)
                  .eq('status', 'published')
                  .eq('is_expired', false);

                if (error) {
                  lastError = new Error(error.message);
                  if (attempt < maxRetries) {
                    console.warn(`[getJobsByIds] Batch ${batchIndex} attempt ${attempt} failed, retrying in ${attempt * 500}ms...`);
                    await new Promise(resolve => setTimeout(resolve, attempt * 500));
                    continue;
                  }
                } else {
                  return data || [];
                }
              } catch (e) {
                lastError = e as Error;
                if (attempt < maxRetries) {
                  console.warn(`[getJobsByIds] Batch ${batchIndex} attempt ${attempt} threw error, retrying...`);
                  await new Promise(resolve => setTimeout(resolve, attempt * 500));
                }
              }
            }
            
            console.error(`[getJobsByIds] Batch ${batchIndex} failed after ${maxRetries} attempts:`, lastError?.message);
            return [];
          })
        );
        
        allResults.push(...batchResults);
      }
      
      const batchResults = allResults;

      // Flatten results and format
      const allJobs = batchResults.flat();

      return allJobs.map(job => ({
        ...job,
        display_location: this.formatDisplayLocation(job.job_locations?.[0])
      }));

    } catch {
      return [];
    }
  }

  /**
   * Get jobs user has already interacted with (to exclude from results)
   * Public so components can use this to filter replenished results
   */
  async getExcludedJobIds(userId: string): Promise<string[]> {
    try {
      const { data, error } = await this.supabaseService
        .getClient()
        .from('job_feedback')
        .select('job_id')
        .eq('user_id', userId)
        .in('feedback_type', ['not_interested', 'applied', 'hidden']);

      if (error) {
        throw error;
      }

      return (data || []).map(item => item.job_id);
    } catch {
      return [];
    }
  }


  /**
   * Format display location from geocoded data
   */
  private formatDisplayLocation(locationData: any): string | null {
    if (!locationData) {
      return null;
    }

    const { city_geocoded, country_geocoded, raw_location_string } = locationData;

    if (city_geocoded && country_geocoded) {
      return `${city_geocoded}, ${country_geocoded}`;
    }

    if (country_geocoded) {
      return country_geocoded;
    }

    if (raw_location_string) {
      return raw_location_string;
    }

    return null;
  }

  /**
   * Update user's profile embedding after significant profile changes
   */
  async updateUserProfileEmbedding(userId: string): Promise<void> {
    try {
      // Get user's profile data
      const { data: userData, error: userError } = await this.supabaseService
        .getClient()
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (userError || !userData) {
        throw new Error('User not found');
      }

      // Get calibration signals
      const calibrationSignals = await this.calibrationService.getSignals(userId);

      // Build profile object
      const profile = {
        extractedText: userData.resume_text || '',
        location: userData.location || '',
        seniority: userData.preferences?.seniority || '',
        targetRoles: userData.preferences?.targetRoles || [],
        skills: userData.skills || [],
        industries: userData.preferences?.industries || [],
        preferredWorkMode: userData.preferences?.preferredWorkMode || ''
      };

      // Generate new embedding
      const embedding = await this.embeddingService.generateProfileEmbedding(
        profile, 
        this.convertCalibrationSignalsFormat(calibrationSignals)
      );

      // Save to database
      await this.embeddingService.saveUserEmbedding(userId, embedding);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Convert calibration signals to format expected by embedding service
   */
  private convertCalibrationSignalsFormat(signals: CalibrationCategory[]): any {
    const converted: any = {};
    
    signals.forEach(category => {
      converted[category.category] = {
        priority: category.priority,
        description: category.description,
        excellentFit: category.excellentFit.map(s => s.text),
        goodFit: category.goodFit.map(s => s.text),
        borderline: category.borderline.map(s => s.text),
        notAFit: category.notAFit.map(s => s.text)
      };
    });

    return converted;
  }

  /**
   * Fetch user's experience data for experience-based scoring
   */
  private async getUserExperience(userId: string): Promise<UserExperience> {
    try {
      const { data: userData, error } = await this.supabaseService
        .getClient()
        .from('users')
        .select('work_history, profile_summary, experience_years, skills')
        .eq('id', userId)
        .single();

      if (error || !userData) {
        return {
          workHistory: [],
          profileSummary: '',
          experienceYears: 0,
          skills: []
        };
      }

      return {
        workHistory: userData.work_history || [],
        profileSummary: userData.profile_summary || '',
        experienceYears: userData.experience_years || 0,
        skills: userData.skills || []
      };
    } catch (error) {
      return {
        workHistory: [],
        profileSummary: '',
        experienceYears: 0,
        skills: []
      };
    }
  }

  /**
   * Calculate experience score based on how well user's work history matches job requirements
   * Returns 0-100 score
   */
  private calculateExperienceScore(
    job: Job, 
    userExperience: UserExperience,
    debug: boolean = false
  ): number {
    let score = 0;
    const matchReasons: string[] = [];

    // Build job text to search against
    const jobText = [
      job.title,
      job.short_description,
      job.full_description,
      ...(job.requirements || []),
      ...(job.responsibilities || [])
    ].filter(Boolean).join(' ').toLowerCase();

    // 1. Work History Matching (up to 50 points)
    // Check if user's previous roles/companies/descriptions match job requirements
    let workHistoryScore = 0;
    for (const work of userExperience.workHistory) {
      const workText = [
        work.title,
        work.company,
        work.description,
        ...(work.responsibilities || []),
        ...(work.achievements || [])
      ].filter(Boolean).join(' ').toLowerCase();

      // Extract key terms from work history
      const workTerms = this.extractKeyTerms(workText);
      
      // Count how many work terms appear in job
      let matchCount = 0;
      for (const term of workTerms) {
        if (jobText.includes(term)) {
          matchCount++;
        }
      }

      // Score based on match percentage
      if (workTerms.length > 0) {
        const matchRate = matchCount / Math.min(workTerms.length, 10);
        workHistoryScore += matchRate * 15; // Each relevant job contributes up to 15 points
      }
    }
    workHistoryScore = Math.min(50, workHistoryScore); // Cap at 50

    if (workHistoryScore > 0) {
      matchReasons.push(`Work history match: ${workHistoryScore.toFixed(0)} points`);
    }
    score += workHistoryScore;

    // 2. Profile Summary Matching (up to 25 points)
    if (userExperience.profileSummary) {
      const summaryTerms = this.extractKeyTerms(userExperience.profileSummary.toLowerCase());
      let summaryMatchCount = 0;
      
      for (const term of summaryTerms) {
        if (jobText.includes(term)) {
          summaryMatchCount++;
        }
      }

      if (summaryTerms.length > 0) {
        const summaryMatchRate = summaryMatchCount / Math.min(summaryTerms.length, 15);
        const summaryScore = Math.min(25, summaryMatchRate * 30);
        score += summaryScore;
        
        if (summaryScore > 5) {
          matchReasons.push(`Profile match: ${summaryScore.toFixed(0)} points`);
        }
      }
    }

    // 3. Experience Years vs Job Level (up to 25 points)
    const jobLevel = (job.level || '').toLowerCase();
    const jobTitle = (job.title || '').toLowerCase();
    const yearsScore = this.calculateYearsLevelMatch(
      userExperience.experienceYears, 
      jobLevel, 
      jobTitle
    );
    score += yearsScore;
    
    if (yearsScore > 0) {
      matchReasons.push(`Experience level match: ${yearsScore.toFixed(0)} points`);
    }

    return Math.min(100, Math.max(0, score));
  }

  /**
   * Extract meaningful key terms from text for matching
   */
  private extractKeyTerms(text: string): string[] {
    // Common words to filter out
    const stopWords = new Set([
      'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with',
      'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have',
      'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should',
      'may', 'might', 'must', 'shall', 'can', 'need', 'dare', 'ought', 'used',
      'i', 'you', 'he', 'she', 'it', 'we', 'they', 'what', 'which', 'who',
      'this', 'that', 'these', 'those', 'am', 'as', 'by', 'from', 'up', 'about',
      'into', 'through', 'during', 'before', 'after', 'above', 'below', 'between',
      'under', 'again', 'further', 'then', 'once', 'here', 'there', 'when', 'where',
      'why', 'how', 'all', 'each', 'few', 'more', 'most', 'other', 'some', 'such',
      'no', 'nor', 'not', 'only', 'own', 'same', 'so', 'than', 'too', 'very',
      'just', 'also', 'now', 'new', 'work', 'worked', 'working', 'company', 'team'
    ]);

    // Extract words, filter short ones and stop words
    const words = text
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length >= 3 && !stopWords.has(word));

    // Return unique terms
    return [...new Set(words)];
  }

  /**
   * Calculate score based on years of experience vs job level requirements
   */
  private calculateYearsLevelMatch(
    experienceYears: number, 
    jobLevel: string, 
    jobTitle: string
  ): number {
    // Map job level to expected years range
    const levelToYears: Record<string, { min: number; ideal: number }> = {
      'entry': { min: 0, ideal: 1 },
      'junior': { min: 0, ideal: 2 },
      'mid': { min: 2, ideal: 4 },
      'mid-level': { min: 2, ideal: 4 },
      'senior': { min: 5, ideal: 7 },
      'lead': { min: 7, ideal: 10 },
      'principal': { min: 8, ideal: 12 },
      'staff': { min: 8, ideal: 12 },
      'manager': { min: 5, ideal: 8 },
      'director': { min: 8, ideal: 12 },
      'vp': { min: 10, ideal: 15 },
      'head': { min: 8, ideal: 12 }
    };

    // Detect level from job level field or title
    let detectedLevel = '';
    const combinedText = `${jobLevel} ${jobTitle}`.toLowerCase();
    
    for (const level of Object.keys(levelToYears)) {
      if (combinedText.includes(level)) {
        detectedLevel = level;
        break;
      }
    }

    // If no level detected, use mid-level as default
    if (!detectedLevel) {
      detectedLevel = 'mid-level';
    }

    const expectedRange = levelToYears[detectedLevel];
    
    // Perfect match: years >= ideal
    if (experienceYears >= expectedRange.ideal) {
      return 25;
    }
    
    // Good match: years >= min
    if (experienceYears >= expectedRange.min) {
      const progress = (experienceYears - expectedRange.min) / 
                       (expectedRange.ideal - expectedRange.min);
      return 15 + (progress * 10); // 15-25 points
    }
    
    // Underqualified but not by much
    if (experienceYears >= expectedRange.min - 1) {
      return 10;
    }
    
    // Significantly underqualified
    return 5;
  }

  /**
   * Pre-classify companies with AI to determine industry match
   * Results are cached in the CalibrationService for use during scoring
   * 
   * Now uses pre-computed metadata from database (populated during job ingestion)
   * This is instant compared to the previous AI classification approach
   */
  private async preClassifyCompanies(jobs: Job[], targetIndustry: string): Promise<void> {
    // Extract unique company names from jobs
    const companyNames: string[] = [];
    const seenCompanies = new Set<string>();
    
    for (const job of jobs) {
      const companyKey = (job.company || '').toLowerCase().trim();
      if (companyKey && !seenCompanies.has(companyKey)) {
        companyNames.push(companyKey);
        seenCompanies.add(companyKey);
      }
    }

    const metadata = await this.companyMetadataService.batchGetMetadata(companyNames);
    
    // Companies with AI in their industries
    const aiCompanies = Array.from(metadata.entries())
      .filter(([_, m]) => m.industries.includes('ai'));
    
    // Jobs from AI companies
    const aiCompanyNames = new Set(aiCompanies.map(([name]) => name));
    const jobsAtAiCompanies = jobs.filter(j => aiCompanyNames.has((j.company || '').toLowerCase().trim()));

    // Store metadata in CalibrationService for use during scoring
    this.calibrationService.setCompanyMetadata(metadata);
  }
}
